import Drawer from "@material-ui/core/Drawer";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuthContext } from "contexts/auth";
import { useTranslation } from "react-i18next";
import {
  FiCalendar,
  FiClipboard,
  FiDollarSign,
  FiMail,
  FiSettings,
  FiTruck,
  FiUser,
  FiPenTool,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import { AiOutlineTool } from "react-icons/ai";

// Move styled component outside to avoid dynamic creation warnings
const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 0.25rem 1rem 0.25rem 2.5rem;
  display: ${(props) => props.$display || "flex"};
  font-size: 1rem;
  :hover {
    background-color: #017195;
  }
`;

const DRAWER_SECTION_STATE_KEY = "navigationDrawerExpandedSections";

const DEFAULT_EXPANDED_SECTIONS = {
  CLIENT: true,
  SCHEDULING: true,
  SALES: true,
  FINANCE: true,
  COMMERCIAL: true,
  WAREHOUSE: true,
  EQUIPMENT: true,
  EMPLOYEES: true,
  COMMUNICATIONS: true,
  MANAGE: true,
};

const getInitialExpandedSections = () => {
  try {
    const savedState = sessionStorage.getItem(DRAWER_SECTION_STATE_KEY);

    return savedState
      ? JSON.parse(savedState)
      : DEFAULT_EXPANDED_SECTIONS;
  } catch {
    return DEFAULT_EXPANDED_SECTIONS;
  }
};

export default function DrawerCon({ anchor, setAnchor, linkClicked }) {
  const { t } = useTranslation();
  const tBase = "views.navigation.drawer";
  const tr = (key) => t(`${tBase}.${key}`);
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState(getInitialExpandedSections);

  const { permissions, company_features = {} } = useAuthContext();
  const allowedPages = permissions.pages || [""];

  const ROUTE_TO_COMPANY_FEATURE = {
    "/outreach": "outreach",
  };

  const isCompanyFeatureAllowed = (route) => {
    const featureKey = ROUTE_TO_COMPANY_FEATURE[route];
    if (!featureKey) return true;
    return !!company_features[featureKey];
  };

  const navShow = (page) => {
    const raw = typeof page === "string" || page instanceof String
      ? page
      : page.pathname;
    const route = "/" + raw.split("/")[1];
    if (!allowedPages.includes(route)) return "none";
    if (!isCompanyFeatureAllowed(route)) return "none";
    return "flex";
  };

  const menuItems = [
    {
      header: "CLIENT",
      icon: <FiUser />,
      links: [
        {
          name: "Customers",
          to: "/customers",
        },
        {
          name: "Properties",
          to: "/property",
        },
        {
          name: "Service Plans",
          to: "/service-plan-dashboard",
        }
      ]
    },
    {
      header: "SCHEDULING",
      icon: <FiCalendar />,
      links: [
        {
          name: "Appointments",
          to: "/appointments",
        },
        {
          name: "Calendar",
          to: "/calendar",
        },
      ]
    },
    {
      header: "SALES",
      icon: <FiClipboard />,
      links: [
        {
          name: "Requests",
          to: "/requests",
        },
        {
          name: "Estimates",
          to: "/estimates",
        },
      ]
    },
    {
      header: "FINANCE",
      icon: <FiDollarSign />,
      links: [
        {
          name: "Invoices",
          to: "/invoices",
        },
        {
          name: "Payments",
          to: "/payments",
        },
      ]
    },
    {
      header: "COMMERCIAL",
      icon: <FiPenTool />,
      links: [
        {
          name: "Projects",
          to: "/projects",
        },
        {
          name: "Quotes",
          to: "/quotes",
        },
      ]
    },
    {
      header: "WAREHOUSE",
      icon: <FiTruck />,
      links: [
        {
          name: "Order",
          to: "/order",
        },
        {
          name: "Warehouse",
          to: "/warehouse",
        },
        {
          name: "Purchasing",
          to: "/purchasing",
        },
        {
          name: "Vendors",
          to: "/vendors",
        },
        {
          name: "Products",
          to: "/products",
        },
      ]
    },
    {
      header: "EQUIPMENT",
      icon: <AiOutlineTool />,
      links: [
        {
          name: "Manage Equipment",
          to: "/equipment",
        },
        {
          name: "Vehicle Check-Out",
          to: "/vehicleCheckout",
        },
        {
          name: "Repair Request Management",
          to: "/repair_request",
        },
        {
          name: "Vehicle Inspection Dashboard",
          to:"/vehicle_inspection/dashboard"
        }
      ]
    },
    {
      header: "EMPLOYEES",
      icon: <FiUser />,
      links: [
        {
          name: "Team Dashboard",
          to: "/team",
        },
        {
          name: "Employees",
          to: "/admin/employees",
        },
        {
          name: "Absent",
          to: "/absent",
        },
        {
          name: "Historical Team",
          to: "/historicalteam",
        },
        {
          name: "Time Clock",
          to: "/time-clock",
        },
        {
          name: "Production",
          to: "/production",
        },
      ]
    },
    {
      header: "COMMUNICATIONS",
      icon: <FiMail />,
      links: [
        {
          name: "Campaigns",
          to: "/outreach/campaigns",
        },
        {
          name: "Templates",
          to: "/outreach/templates",
        },
        {
          name: "Unsubscribed",
          to: "/outreach/unsubscribed",
        },
      ]
    },
    {
      header: "MANAGE",
      icon: <FiSettings />,
      links: [
        {
          name: "Question Builder",
          to: "/settings/questions",
        },
        {
          name: "Warehouse Management",
          to: "/warehouseManagement",
        },
        {
          name: "Category Management",
          to: "/category_management",
        },
        {
          name: "Reports",
          to: "/reports",
        },
        {
          name: "Settings",
          to:"/admin"
        }
      ]
    },
  ]

  const getExpandedSectionForPath = (pathname) => {
    const normalizedPath = pathname.replace(/\/$/, "");
    const matchingItem = menuItems.find((item) =>
      item.links.some((link) => {
        const normalizedLink = link.to.replace(/\/$/, "");
        return (
          normalizedPath === normalizedLink ||
          normalizedPath.startsWith(`${normalizedLink}/`)
        );
      })
    );
    return matchingItem?.header;
  };

  useEffect(() => {
    const activeSection = getExpandedSectionForPath(location.pathname);
    if (activeSection) {
      setExpandedSections((prev) =>
        prev[activeSection]
          ? prev
          : {
              ...prev,
              [activeSection]: true,
            }
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    sessionStorage.setItem(
      DRAWER_SECTION_STATE_KEY,
      JSON.stringify(expandedSections)
    );
  }, [expandedSections]);

  const toggleSection = (header) => {
      setExpandedSections((prev) => ({
        ...prev,
        [header]: !prev[header],
      }));
    };

    const visibleMenuItems = menuItems.filter((item) =>
      item.links.some((link) => navShow(link.to) === "flex")
  );

  const areAllSectionsExpanded = visibleMenuItems.every(
    (item) => expandedSections[item.header]
  );

  const expandAllSections = () => {
    setExpandedSections((prev) => {
      const updated = { ...prev };

      visibleMenuItems.forEach((item) => {
        updated[item.header] = true;
      });

      return updated;
    });
  };

  const collapseAllSections = () => {
    setExpandedSections((prev) => {
      const updated = { ...prev };

      visibleMenuItems.forEach((item) => {
        updated[item.header] = false;
      });

      return updated;
    });
  };

  return (
    <Drawer anchor="left" open={anchor} onClose={() => setAnchor(false)}>
      <div className="scrollbar">
        <div className="min-h-screen pb-12 text-white bg-slate-900">
          <div className="flex p-4 space-x-2 justify-between">
            <div className="text-xl text-primary">EServ</div>
            <div className="flex justify-end px-4 py-2">
            <button
              type="button"
              className="text-xs text-slate-300 hover:text-white"
              onClick={
                areAllSectionsExpanded
                  ? collapseAllSections
                  : expandAllSections
              }
            >
              {areAllSectionsExpanded ? tr("Collapse All") : tr("Expand All")}
            </button>
          </div>
          </div>
          <div className="scrollbar:!w-1.5 w-[300px] flex flex-col space-y-4">
            {visibleMenuItems
              .filter((item) => item.links.some((link) => navShow(link.to) === "flex"))
              .map((item) => {
                const isExpanded = expandedSections[item.header];

                return (
                  <div key={item.header}>
                    <button
                      type="button"
                      className="flex items-center justify-between w-full px-4 py-1 text-sm bg-transparent border-0 cursor-pointer text-slate-300 hover:bg-[#017195]"
                      aria-expanded={isExpanded}
                      onClick={() => toggleSection(item.header)}
                    >
                      <span className="flex items-center space-x-2">
                        {item.icon}
                        <span>{tr(item.header)}</span>
                      </span>
                      {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                    </button>
                    {isExpanded &&
                      item.links.map((link) => (
                        <NavLink
                          key={link.to}
                          name={link.name}
                          to={link.to}
                          $display={navShow(link.to)}
                          onClick={() => linkClicked(link.name)}
                        >
                          {tr(link.name)}
                        </NavLink>
                      ))}
                  </div>
                );
              })}
          </div>
        </div>
      </div>  
    </Drawer>
  );
}
